import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import styles from '../static/styles/BundleTasks.module.css';
import { fetchBundleTasks } from '../api/bundle';
import { isAuthenticated } from '../api/auth';
import MessagePopup from '../components/MessagePopup';
import BundleSplash from '../components/BundleSplash';
import BundleTaskSubmit from '../components/BundleTaskSubmit';
import eslCoinIcon from '../static/img/DailyCheckin/eslCoin.png';
import {
  CHALLENGE_STATUS,
  getBundle,
  hasSeenSplash,
  isTaskActionable,
  markSplashSeen,
  readStoredBundleResult,
  resolveTaskBundleAccess,
  storeBundleResult,
  summariseTasks,
} from '../utils/bundles';

const TRANSPARENT_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const STATUS_LABELS = {
  [CHALLENGE_STATUS.PENDING]: 'In review',
  [CHALLENGE_STATUS.ACCEPTED]: 'Completed',
  [CHALLENGE_STATUS.REJECTED]: 'Rejected - submit again',
};

const STATUS_CLASSES = {
  [CHALLENGE_STATUS.PENDING]: styles.taskPending,
  [CHALLENGE_STATUS.ACCEPTED]: styles.taskCompleted,
  [CHALLENGE_STATUS.REJECTED]: styles.taskRejected,
};

const BundleTasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bundleCode = resolveTaskBundleAccess(location.state);
  const bundle = getBundle(bundleCode);

  const [data, setData] = useState(
    () => location.state?.bundle || (bundleCode ? readStoredBundleResult(bundleCode) : null)
  );
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState(null);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [popupMessage, setPopupMessage] = useState('');
  const [submitTask, setSubmitTask] = useState(null);
  const hasIntro = !!(bundle?.congratsVideo || bundle?.splashVideo);
  const [showSplash, setShowSplash] = useState(() => hasIntro && !hasSeenSplash(bundleCode));

  const isAllowed = !!bundleCode;

  const dismissSplash = () => {
    markSplashSeen(bundleCode);
    setShowSplash(false);
  };

  const tasks = useMemo(() => (Array.isArray(data?.tasks) ? data.tasks : []), [data]);
  const { total, completed, creditsTotal, percent, allCompleted } = useMemo(
    () => summariseTasks(tasks),
    [tasks]
  );
  const nextTask = useMemo(() => tasks.find(isTaskActionable) || null, [tasks]);

  const loadTasks = useCallback(async () => {
    if (!bundleCode) {
      setError('No bundle selected.');
      setLoading(false);
      return;
    }

    const response = await fetchBundleTasks(bundleCode);
    if (response?.success) {
      storeBundleResult(bundleCode, response);
      setData(response);
      setError(null);
    } else {
      setData(prev => {
        if (!prev) setError(response?.message || 'We could not load your tasks right now.');
        return prev;
      });
    }
    setLoading(false);
  }, [bundleCode]);

  useEffect(() => {
    document.title = bundle ? `${bundle.game} Missions | Jazz ESL` : 'Bundle Missions | Jazz ESL';
  }, [bundle]);

  useEffect(() => {
    if (!isAuthenticated()) {
      const handleAuthComplete = () => loadTasks();
      window.addEventListener('authComplete', handleAuthComplete);
      return () => window.removeEventListener('authComplete', handleAuthComplete);
    }

    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!activeTaskId && nextTask) {
      setActiveTaskId(nextTask.challengeId);
    }
  }, [activeTaskId, nextTask]);

  const goToClaim = useCallback(() => {
    navigate('/bundle-vouchers', {
      replace: true,
      state: { bundleCode, redemptionId: data?.redemption_id, bundle: data },
    });
  }, [navigate, bundleCode, data]);

  useEffect(() => {
    if (!allCompleted || showSplash || submitTask) return;
    goToClaim();
  }, [allCompleted, showSplash, submitTask, goToClaim]);

  const handleStartTask = (task) => {
    if (!task) return;

    if (!isTaskActionable(task)) {
      setPopupMessage(
        Number(task.challengeStatus) === CHALLENGE_STATUS.ACCEPTED
          ? 'You have already completed this task.'
          : 'This entry is with our team for review. We will update you shortly.'
      );
      return;
    }

    setActiveTaskId(task.challengeId);
    setSubmitTask(task);
  };

  const handleSubmitted = (message) => {
    setSubmitTask(null);
    setPopupMessage(message);
    loadTasks();
  };

  const themeStyle = bundle
    ? {
        '--bundle-accent': bundle.theme.accent,
        '--bundle-accent-dark': bundle.theme.accentDark,
        '--bundle-accent-text': bundle.theme.accentText,
        '--bundle-glow': bundle.theme.glow,
        '--bundle-soft': bundle.theme.soft,
        '--bundle-page': bundle.theme.page,
        '--bundle-top-gradient': bundle.theme.topGradient,
        '--bundle-top-gradient-height': bundle.theme.topGradientHeight,
        '--bundle-card': bundle.theme.card,
        '--bundle-card-border': bundle.theme.cardBorder,
        '--bundle-reward-border': bundle.theme.rewardBorder,
        '--bundle-pill': bundle.theme.pill,
        '--bundle-pill-text': bundle.theme.pillText,
        '--bundle-card-pending-border': bundle.theme.cardPendingBorder,
        '--bundle-card-completed': bundle.theme.cardCompleted,
        '--bundle-card-completed-border': bundle.theme.cardCompletedBorder,
        '--bundle-pill-completed': bundle.theme.pillCompleted,
        '--bundle-pill-completed-text': bundle.theme.pillCompletedText,
        '--bundle-completed-text': bundle.theme.completedText,
      }
    : undefined;

  const splash = showSplash && hasIntro
    ? <BundleSplash bundle={bundle} onContinue={dismissSplash} />
    : null;

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className={styles.container} style={themeStyle}>
        <div className={styles.stateBox}>
          <span className={styles.spinner} />
          <p>Loading your missions...</p>
        </div>
        {splash}
      </div>
    );
  }

  if (error || !tasks.length) {
    return (
      <div className={styles.container} style={themeStyle}>
        <div className={styles.stateBox}>
          <h2 className={styles.stateTitle}>Nothing to do here yet</h2>
          <p>{error || 'This bundle has no tasks assigned right now.'}</p>
          <button type="button" className={styles.cta} onClick={() => navigate('/')}>
            GO TO HOME
          </button>
        </div>
        {splash}
      </div>
    );
  }

  return (
    <div className={styles.container} style={themeStyle}>

      {splash}

      {/* PROGRESS */}
      <div className={styles.progressHeader}>
        <div className={styles.progressAmount}>
          <strong>{completed}</strong>
          <span className={styles.progressTotal}>/ {total} tasks done</span>
        </div>
        <span className={styles.progressPercent}>{percent}%</span>
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>

      {/* HEADING */}
      <p className={styles.eyebrow}>
        <svg className={styles.bolt} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z" />
        </svg>
        {bundle?.game || 'ESL'} &middot; {bundle?.missions || 'MISSIONS'}
      </p>

      {/* REWARD SUMMARY */}
      <div className={styles.rewardCard}>
        <span className={styles.rewardLabel}>
          FINISH ALL {total} TASKS
          {creditsTotal > 0 && (
            <span className={styles.rewardSub}>Plus {creditsTotal} ESL credits along the way</span>
          )}
        </span>
        <span className={styles.rewardValue}>+{data?.reward || 'Bundle reward'}</span>
      </div>

      {/* TASKS */}
      <ul className={styles.taskList}>
        {tasks.map((task) => {
          const status = Number(task.challengeStatus);
          const isActive = task.challengeId === activeTaskId && isTaskActionable(task);

          return (
            <li key={task.challengeId}>
              <button
                type="button"
                className={[
                  styles.taskCard,
                  STATUS_CLASSES[status] || '',
                  isActive ? styles.taskActive : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleStartTask(task)}
              >
                <img
                  className={styles.taskThumb}
                  src={task.challengeThumbnail}
                  alt=""
                  loading="lazy"
                  onError={(e) => {
                    if (e.currentTarget.src !== TRANSPARENT_PX) e.currentTarget.src = TRANSPARENT_PX;
                  }}
                />
                <span className={styles.taskText}>
                  <span className={styles.taskTitle}>{task.challengeObjective}</span>
                  <span className={styles.taskSubtitle}>
                    {STATUS_LABELS[status] || task.challengeName}
                  </span>
                </span>
                <span className={styles.taskPrize}>
                  <img className={styles.coinIcon} src={eslCoinIcon} alt="" />
                  +{task.challengePrize}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* CTA  */}
      <div className={styles.ctaBar} hidden={!!submitTask}>
        <button
          type="button"
          className={styles.cta}
          onClick={allCompleted ? goToClaim : () => handleStartTask(nextTask)}
          disabled={!allCompleted && !nextTask}
        >
          {allCompleted
            ? 'CLAIM YOUR REWARD →'
            : nextTask
              ? 'START NEXT TASK →'
              : 'TASKS UNDER REVIEW'}
        </button>
      </div>

      {submitTask && (
        <BundleTaskSubmit
          task={submitTask}
          bundle={bundle}
          onClose={() => setSubmitTask(null)}
          onSubmitted={handleSubmitted}
        />
      )}

      <MessagePopup
        isOpen={!!popupMessage}
        message={popupMessage}
        onClose={() => setPopupMessage('')}
      />
    </div>
  );
};

export default BundleTasks;
